import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, Upload, ShieldCheck, Archive, Copy, Users, MoreHorizontal, Pencil, Eye, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import {
  academyListCourses, academyImport, academyPublish, academyArchive, academyNewDraft,
  academyReassignAfterPublish, academyDelete, academyDedupeAssignments,
} from '../../../services/api';

/**
 * Admin → Lutrom Academy → Course Management.
 *
 * Lists every course version (draft / published / archived), exposes
 * the import flow, and links each row through to Preview / Assign /
 * Results. Published rows are IMMUTABLE — the only way to change them
 * is via "New draft version" which clones them into a fresh draft.
 */
export default function CourseManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await academyListCourses());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load courses');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please upload a .zip package');
      return;
    }
    setImporting(true);
    setImportReport(null);
    try {
      const rep = await academyImport(f);
      setImportReport(rep);
      if (rep.ok) {
        toast.success(`Imported ${rep.title} v${rep.version} as draft`);
        await load();
      } else {
        toast.error(`Import failed — ${rep.errors?.length || 0} errors`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const doPublish = async (r) => {
    const summary = window.prompt(
      `Publish ${r.title} v${r.version}?\n\nOptional changelog (visible to admins):`,
      '',
    );
    if (summary === null) return;
    try {
      await academyPublish(r.courseId, r.version, summary);
      toast.success('Published — assignable to employees');
      // Reassignment prompt: if this is not the first version, ask the
      // admin what to do with employees who completed a prior version.
      const priorPublished = rows.filter(
        x => x.courseId === r.courseId && x.version !== r.version && x.status !== 'draft',
      );
      if (priorPublished.length > 0) {
        const mode = window.prompt(
          `Employees have completed ${r.courseId} on an earlier version.\n\n` +
          `What should happen?\n` +
          `  A - No action, existing completion remains valid\n` +
          `  B - Assign the new version at their normal refresher date\n` +
          `  C - Reassign the new version now\n` +
          `  D - Cancel here and pick specific employees on the Assign page\n\n` +
          `Type A, B, C or D:`, 'A');
        const m = (mode || '').trim().toUpperCase();
        if (['A', 'B', 'C'].includes(m)) {
          try {
            const res = await academyReassignAfterPublish(r.courseId, r.version, m);
            toast.success(`Reassignment (${m}): ${res.reassigned_now} now, ${res.scheduled_for_refresher} at refresher`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Reassignment failed');
          }
        } else if (m === 'D') {
          window.location.href = `/admin/academy/${r.courseId}/${r.version}/assign`;
          return;
        }
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    }
  };

  const doArchive = async (r) => {
    if (!window.confirm(`Archive ${r.title} v${r.version}? Historical records stay intact.`)) return;
    await academyArchive(r.courseId, r.version);
    toast.success('Archived');
    await load();
  };

  const doNewDraft = async (r) => {
    try {
      const res = await academyNewDraft(r.courseId, r.version, '');
      toast.success(`Created draft v${res.version} — edit and publish when ready`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create draft');
    }
  };

  const doDelete = async (r) => {
    if (!window.confirm(
      `Permanently delete ${r.title} v${r.version}?\n\n` +
      `This removes the course, its assets, assignments, in-flight progress, attempts and acknowledgements. ` +
      `Certificates already issued for this version are PRESERVED as compliance records — learners keep their PDF ` +
      `and cert numbers remain resolvable after the course is gone.`
    )) return;
    try {
      const res = await academyDelete(r.courseId, r.version);
      if (res?.certificates_preserved) {
        toast.success(`Course deleted · ${res.certificates_preserved} certificate${res.certificates_preserved === 1 ? '' : 's'} preserved`);
      } else {
        toast.success('Course version deleted');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete course');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="academy-course-management">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
            <GraduationCap className="h-3.5 w-3.5" />
            Lutrom Academy
          </div>
          <h1 className="text-2xl font-display font-bold text-slate-900">Course Management</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Upload a Lutrom Course Package (.zip), preview it, then publish so
            employees can be assigned. Published versions are locked — content
            edits create a new version.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm(
                'Scan for duplicate learner assignments and remove the extras?\n\n' +
                'The earliest assignment for each learner+course+version is kept. ' +
                'Progress, attempts, acknowledgements and certificates are preserved.'
              )) return;
              try {
                const r = await academyDedupeAssignments();
                if (r.assignments_deleted === 0) {
                  toast.success('No duplicates found — everything is clean.');
                } else {
                  toast.success(
                    `Removed ${r.assignments_deleted} duplicate assignment${r.assignments_deleted === 1 ? '' : 's'} ` +
                    `across ${r.duplicate_groups} learner${r.duplicate_groups === 1 ? '' : 's'}.`
                  );
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Cleanup failed');
              }
            }}
            data-testid="academy-dedupe-assignments-btn"
          >
            <Copy className="h-4 w-4 mr-1.5" />
            Clean duplicate assignments
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              accept=".zip"
              onChange={onImport}
              className="hidden"
              data-testid="academy-import-file"
              disabled={importing}
            />
            <Button asChild data-testid="academy-import-btn">
              <span className="cursor-pointer inline-flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {importing ? 'Importing…' : 'Import course'}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {importReport && !importReport.ok && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 p-4 space-y-1"
          data-testid="academy-import-report-errors"
        >
          <div className="font-medium text-rose-800">
            Import failed — {importReport.errors?.length || 0} errors
          </div>
          <ul className="text-xs text-rose-700 space-y-0.5 list-disc pl-4">
            {(importReport.errors || []).slice(0, 30).map((e, i) => (
              <li key={i}><b>{e.path}</b>: {e.message}</li>
            ))}
          </ul>
          {(importReport.warnings || []).length > 0 && (
            <>
              <div className="mt-2 text-amber-800 font-medium text-sm">Warnings</div>
              <ul className="text-xs text-amber-700 space-y-0.5 list-disc pl-4">
                {importReport.warnings.slice(0, 10).map((e, i) => (
                  <li key={i}><b>{e.path}</b>: {e.message}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {importReport && importReport.ok && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 p-4"
          data-testid="academy-import-report-ok"
        >
          <div className="font-medium text-emerald-900">
            Imported {importReport.title} v{importReport.version} as draft
          </div>
          <div className="text-xs text-emerald-800 mt-1">
            {importReport.modules} modules · {importReport.screens} screens ·
            {' '}{importReport.assessment_bank_size} question bank ·
            {' '}{importReport.pass_mark}% pass mark
          </div>
        </div>
      )}

      {/* Mobile card list — portrait phones */}
      <div className="md:hidden space-y-3" data-testid="academy-course-cards">
        {loading && (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-slate-500">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-slate-500">
            No courses yet. Tap <b>Import course</b> to upload the first .zip.
          </div>
        )}
        {rows.map((r) => (
          <div
            key={`m::${r.courseId}::${r.version}`}
            className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
            data-testid={`academy-course-card-${r.courseId}-${r.version}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{r.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  v{r.version} · {r.category}
                </div>
              </div>
              <Badge variant={r.status === 'published' ? 'default' : r.status === 'archived' ? 'outline' : 'secondary'}>
                {r.status}
              </Badge>
            </div>
            <div className="text-xs text-slate-500">
              Imported {r.importedAt ? new Date(r.importedAt).toLocaleDateString() : '—'}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild size="sm" className="flex-1"
                data-testid={`academy-preview-mobile-${r.courseId}-${r.version}`}>
                <Link to={`/admin/academy/${r.courseId}/${r.version}/preview`}>
                  <Eye className="h-4 w-4 mr-1.5" />
                  Preview
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="px-2"
                    data-testid={`academy-more-mobile-${r.courseId}-${r.version}`}
                    aria-label="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {r.status === 'draft' && (
                    <>
                      <DropdownMenuItem asChild data-testid={`academy-edit-mobile-${r.courseId}-${r.version}`}>
                        <Link to={`/admin/academy/${r.courseId}/${r.version}/edit`}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => doPublish(r)}
                        data-testid={`academy-publish-mobile-${r.courseId}-${r.version}`}>
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        Publish
                      </DropdownMenuItem>
                    </>
                  )}
                  {r.status === 'published' && (
                    <>
                      <DropdownMenuItem asChild data-testid={`academy-assign-mobile-${r.courseId}-${r.version}`}>
                        <Link to={`/admin/academy/${r.courseId}/${r.version}/assign`}>
                          <Users className="h-4 w-4 mr-2" />
                          Assign
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => doNewDraft(r)}
                        data-testid={`academy-new-draft-mobile-${r.courseId}-${r.version}`}>
                        <Copy className="h-4 w-4 mr-2" />
                        New draft
                      </DropdownMenuItem>
                    </>
                  )}
                  {r.status !== 'archived' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => doArchive(r)}
                        data-testid={`academy-archive-mobile-${r.courseId}-${r.version}`}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => doDelete(r)}
                    className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                    data-testid={`academy-delete-mobile-${r.courseId}-${r.version}`}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete permanently
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>

      {/* Table view — landscape / tablet / desktop */}
      <div className="hidden md:block rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Course</th>
              <th className="text-left px-3 py-2">Version</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Imported</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                No courses yet. Click <b>Import course</b> to upload the first .zip.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr
                key={`${r.courseId}::${r.version}`}
                className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`academy-course-row-${r.courseId}-${r.version}`}
              >
                <td className="px-3 py-2 font-medium text-slate-900">{r.title}</td>
                <td className="px-3 py-2 tabular-nums">{r.version}</td>
                <td className="px-3 py-2 text-slate-600">{r.category}</td>
                <td className="px-3 py-2">
                  <Badge variant={r.status === 'published' ? 'default' : r.status === 'archived' ? 'outline' : 'secondary'}>
                    {r.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {r.importedAt ? new Date(r.importedAt).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button asChild size="sm" variant="outline"
                    data-testid={`academy-preview-${r.courseId}-${r.version}`}>
                    <Link to={`/admin/academy/${r.courseId}/${r.version}/preview`}>Preview</Link>
                  </Button>
                  {r.status === 'draft' && (
                    <>
                      <Button asChild size="sm" variant="outline"
                        data-testid={`academy-edit-${r.courseId}-${r.version}`}>
                        <Link to={`/admin/academy/${r.courseId}/${r.version}/edit`}>Edit</Link>
                      </Button>
                      <Button size="sm" onClick={() => doPublish(r)}
                        data-testid={`academy-publish-${r.courseId}-${r.version}`}>
                        <ShieldCheck className="h-3 w-3 mr-1" />
                        Publish
                      </Button>
                    </>
                  )}
                  {r.status === 'published' && (
                    <>
                      <Button asChild size="sm" variant="outline"
                        data-testid={`academy-assign-${r.courseId}-${r.version}`}>
                        <Link to={`/admin/academy/${r.courseId}/${r.version}/assign`}>
                          <Users className="h-3 w-3 mr-1" />Assign
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => doNewDraft(r)}
                        data-testid={`academy-new-draft-${r.courseId}-${r.version}`}>
                        <Copy className="h-3 w-3 mr-1" />
                        New draft
                      </Button>
                    </>
                  )}
                  {r.status !== 'archived' && (
                    <Button size="sm" variant="ghost" onClick={() => doArchive(r)}
                      data-testid={`academy-archive-${r.courseId}-${r.version}`}>
                      <Archive className="h-3 w-3 mr-1" />
                      Archive
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => doDelete(r)}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`academy-delete-${r.courseId}-${r.version}`}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
